import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  Router,
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  AuthConfigService,
  authProviders,
  bffCredentialsInterceptor,
  type BffAuthConfig,
} from '@backoffice-frontend/shared/auth/util-oauth';
import { TailwindThemeService } from '@backoffice-frontend/shared/ui-tailwind';
import {
  LocaleProvider,
  translocoProviders,
} from '@backoffice-frontend/shared/util-transloco';
import { EnvConfig } from '@clean-code/models/common';
import { authErrorInterceptor } from '@clean-code/shared/auth/util-auth';
import {
  SplashScreenService,
  SplashScreenState,
} from '@clean-code/shared/common';
import { MatPaginationIntlService } from '@clean-code/shared/components/ui-mat-table';
import { SideNavService } from '@clean-code/shared/templates/ui-tailwind-full-width';
import { provideConfig } from '@clean-code/shared/util-config';
import { dateHttpInterceptor } from '@clean-code/shared/util-graphql';
import {
  GlobalErrorHandlerService,
  errorInterceptorService,
} from '@clean-code/shared/util/util-error';
import {
  USER_LIMIT_SERVICE,
  UserLimitPersonalService,
} from '@clean-code/user-limit/domain';
import { RxState } from '@rx-angular/state';
import * as Sentry from '@sentry/angular-ivy';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { appRoutes } from './app-routing.module';
import { AppModule } from './app.module';

export function appInitializerFactory(
  authConfigService: AuthConfigService,
  splashScreenService: SplashScreenService,
) {
  return async () => {
    try {
      await authConfigService.initAuth();
    } finally {
      splashScreenService.hideState();
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    providers(),
    translocoProviders,
    provideAnimationsAsync(),
    provideConfig(environment as EnvConfig),

    importProvidersFrom(AppModule, NgxPermissionsModule.forRoot()),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        bffCredentialsInterceptor,
        authErrorInterceptor,
        // graphqlErrorInterceptor,
        dateHttpInterceptor,
        errorInterceptorService,
      ]),
    ),
    // provideServiceWorker('ngsw-worker.js', {
    //   enabled: !isDevMode(),
    //   registrationStrategy: 'registerWhenStable:30000',
    // }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      // withDebugTracing(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideToastr(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};

function providers(): Array<unknown> {
  return [
    ...authProviders(environment.auth as unknown as BffAuthConfig),
    RxState,
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(
        inject(AuthConfigService),
        inject(SplashScreenService),
      );
      return initializerFn();
    }),
    LocaleProvider,
    TailwindThemeService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    ...(environment.enableSentry
      ? [
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: true,
            }),
          },
        ]
      : []),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginationIntlService,
    },
    {
      provide: USER_LIMIT_SERVICE,
      useClass: UserLimitPersonalService,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    SideNavService,
    RxState<SplashScreenState>,
  ];
}
